import React, { useState } from 'react';
import { Flex } from '@ui-library/misc';
import classes from './accountDetails.module.scss';
import { duotone } from '@fortawesome/fontawesome-svg-core/import.macro';
import Button from '@ui-library/button/index';
import SetupAccountHeader from '../header';
import Input from '@ui-library/input/index';
import { NewLabel } from '@ui-library/typography';
import { SelectV1, MultiSelectSearch } from '@ui-library/select';
import { useHistory } from 'react-router-dom';
import { Path } from '@components/routes';
import { CatchErr } from '@utils/types';
import useStores from '@stores/useStores';
import { Checkbox } from 'antd';

const ANOTHER_CDP = 'Another CDP/data pipeline';
const otherOption = 'Other';
const somethingElse = 'Something else';
interface IOption {
  label: string;
  value: string;
  search: string;
}

const AccountDetails = () => {
  const history = useHistory();
  const { userStore, organizationStore } = useStores();
  const [migration, setMigration] = useState<string | undefined>(undefined);
  const [otherMigration, setOtherMigration] = useState('');
  const [churn, setChurn] = useState<string | undefined>(undefined);
  const [otherChurn, setOtherChurn] = useState('');
  const [work, setWork] = useState<string[]>([]);
  const [otherWork, setOtherWork] = useState('');

  const goToHome = async () => {
    await userStore?.trackUserLogin({
      firstTimeLogin: true,
      invited: false,
    });
    history.push(Path.HOME);
  };
  const migratedFrom = migration === ANOTHER_CDP ? otherMigration : migration;
  const churnSource = churn === otherOption ? otherChurn : churn;
  const otherWorkSource = work.includes(somethingElse) ? otherWork : true;
  const handleSubmit = async () => {
    try {
      await userStore?.trackUserLogin({
        churnSource,
        migratedFrom,
        workSource: work,
        ...(otherWorkSource ? { otherWorkSourc: work } : {}),
        firstTimeLogin: true,
        invited: false,
      });
      history.push(Path.HOME);
    } catch (error: CatchErr) {
      window.rudderanalytics.track('signup up failure', {
        name: userStore!.name,
        organization: organizationStore!.name,
        freetrial: organizationStore!.isFreeTrial,
        error,
      });
    }
  };

  const enableButton = migratedFrom && churnSource && work.length > 0 && otherWorkSource;

  const migrationOptions = [
    'Segment',
    'MParticle',
    'Kafka',
    'Kinesis',
    ANOTHER_CDP,
    'Not using',
  ].map((value) => ({ label: value, value }));
  const churnOptions = [
    'Online search',
    'Podcast',
    'Social media (LinkedIn, YouTube, Twitter)',
    'Word of mouth / referral',
    'Online ads',
    'Event',
    'White Paper',
    otherOption,
  ].map((value) => ({ label: value, value }));

  const workOptions = [
    'Engineering',
    'Marketing',
    'Founder/Executive',
    'Product',
    'Analytics',
    somethingElse,
  ].map((value) => ({ label: value, value, search: value }));

  const handleCheckbox = (option: IOption) => {
    if (work.includes(option.value)) {
      const tempWork = [...work].filter((i) => i !== option.value);
      setWork(tempWork);
    } else {
      setWork([...work, option.value]);
    }
  };

  const renderOption = (option: IOption) => (
    <Checkbox checked={work.includes(option.value)} onChange={() => handleCheckbox(option)}>
      {option.value}
    </Checkbox>
  );

  return (
    <Flex flexDirection="column">
      <SetupAccountHeader
        icon={duotone('comment-lines')}
        header="Tell us a bit about yourself"
        description="This will help us create an experience just for you."
      />

      <div className={classes.marginBottom}>
        <NewLabel>Are you using a similar product?</NewLabel>
        <SelectV1
          placeholder="I am using ..."
          onChange={(val) => setMigration(val)}
          value={migration}
          options={migrationOptions}
        />
      </div>
      {migration === ANOTHER_CDP ? (
        <Input
          id="text-input-name"
          className={classes.marginBottom}
          label="Which one?"
          placeholder="Enter"
          onChange={(e) => setOtherMigration(e.target.value)}
          maxLength={50}
          value={otherMigration}
        />
      ) : null}
      <div className={classes.marginBottom}>
        <NewLabel>How did you hear about RudderStack?</NewLabel>
        <SelectV1
          placeholder="I heard from..."
          onChange={(val) => setChurn(val)}
          value={churn}
          options={churnOptions}
        />
      </div>
      {churn === otherOption && (
        <Input
          label="Give us a few details"
          className={classes.marginBottom}
          placeholder="Enter"
          onChange={(e) => setOtherChurn(e.target.value)}
          maxLength={50}
          value={otherChurn}
        />
      )}
      <div className={classes.marginBottom}>
        <NewLabel>What best describes the work you do?</NewLabel>
        <MultiSelectSearch
          showArrow
          renderOption={renderOption}
          placeholder="Select all that apply"
          dropdownClass={classes.minHeight}
          selectedOptions={work}
          onChange={setWork}
          options={workOptions}
        />
      </div>
      {work.includes(somethingElse) && (
        <Input
          label="Please specify"
          className={classes.marginBottom}
          placeholder="Enter"
          onChange={(e) => setOtherWork(e.target.value)}
          maxLength={50}
          value={otherWork}
        />
      )}
      <Button disabled={!enableButton} className={classes.marginBottom} onClick={handleSubmit}>
        Let's go
      </Button>
      <Flex justifyContent="center">
        <Button onClick={goToHome} textPrimary>
          Skip
        </Button>
      </Flex>
    </Flex>
  );
};

export default AccountDetails;
